import axios from 'axios';

export const BASE_URL = 'https://backend.billswift.cloud';
//export const BASE_URL = 'http://localhost:3000';

const isApiDebugEnabled = () =>
  typeof window !== 'undefined' &&
  process.env.NODE_ENV !== 'production' &&
  window.localStorage?.getItem('disable_api_debug_logs') !== 'true';

const getDebugStack = () =>
  new Error().stack
    ?.split('\n')
    .slice(2, 10)
    .map((line) => line.trim())
    .join('\n');

const getFetchDebugUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input?.url) return input.url;
  return '';
};

if (typeof window !== 'undefined' && !window.__apiDebugFetchPatched) {
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = (input, init) => {
      const url = getFetchDebugUrl(input);
      const shouldLog =
        isApiDebugEnabled() &&
        (url.startsWith(BASE_URL) || url.includes('/api/'));

      if (shouldLog) {
        console.debug('[api-debug] fetch request', {
          method: init?.method || input?.method || 'GET',
          url,
          stack: getDebugStack()
        });
      }

      return originalFetch(input, init);
    };
    window.__apiDebugFetchPatched = true;
  }
}

const api = axios.create({
  baseURL: BASE_URL,
});

// Add a request interceptor to include the Bearer token from 'auth_user'
api.interceptors.request.use(
  (config) => {
    if (isApiDebugEnabled()) {
      console.debug('[api-debug] axios request', {
        method: config.method?.toUpperCase() || 'GET',
        url: `${config.baseURL || ''}${config.url || ''}`,
        params: config.params,
        stack: getDebugStack()
      });
    }

    const authDataRaw = localStorage.getItem('auth_user');
    let token;
    if (authDataRaw) {
      try {
        const authData = JSON.parse(authDataRaw);
        token = authData?.accessToken;
      } catch (e) {
        token = undefined;
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor:
// If the backend answers 401/403 (session expired / unauthorized / account blocked),
// clear auth data and force the user back to the login page with an appropriate reason.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const errorData = error?.response?.data;
    const errorCode = errorData?.code || errorData?.errorCode;
    const errorMsg = typeof errorData === 'string' ? errorData : (errorData?.message || '');

    const isAccountBlocked =
      errorCode === 'ACCOUNT_BLOCKED' ||
      (typeof errorMsg === 'string' &&
        (errorMsg.toLowerCase().includes('suspendu') ||
          errorMsg.toLowerCase().includes('bloqu')));

    if (isAccountBlocked) {
      const displayMsg =
        typeof errorMsg === 'string' && errorMsg && !errorMsg.includes('Forbidden')
          ? errorMsg
          : 'Ce compte a été suspendu par un administrateur.';

      try {
        localStorage.removeItem('auth_user');
        sessionStorage.setItem('blocked_account_message', displayMsg);
      } catch (e) {
        // ignore storage errors
      }

      // If global security modal handler is active, trigger it so the modal can be viewed
      if (typeof window !== 'undefined' && typeof window.__handleForcedLogout === 'function') {
        window.__handleForcedLogout(displayMsg);
      } else {
        const currentPath = window.location.pathname || '';
        if (!currentPath.startsWith('/sessions')) {
          setTimeout(() => {
            window.location.href = '/sessions/signin?blocked=1';
          }, 3500);
        }
      }
    } else if (status === 401 || status === 403) {
      try {
        // Remove only auth info; keep other localStorage keys if any
        localStorage.removeItem('auth_user');
      } catch (e) {
        // ignore localStorage errors
      }

      // Avoid redirect loop if already on a sessions route
      const currentPath = window.location.pathname || '';
      if (!currentPath.startsWith('/sessions')) {
        window.location.href = '/sessions/signin';
      }
    }

    return Promise.reject(error);
  }
);

export default api; 
